import { z } from 'zod';

/**
 * Drone-capability controlled vocabulary — Drone D1 #317 marketplace data model.
 *
 * Mirrors the operator-action verb-FK anti-clobber pattern (Task #90 / migration
 * 0045) and the IMS-role / agency-kind lookups (migrations 0083 / 0084): each
 * canonical list is THIS const-tuple; the DB enforces it via an FK into a
 * `valid_drone_*` / `valid_operator_*` lookup table (migration 0089) — NOT a CHECK
 * enum, so parallel branches (and the later marketplace phases #318–#322) can extend
 * a vocabulary with ONE idempotent INSERT row instead of colliding on a constraint
 * rewrite. A new value = one literal here + one seed INSERT in a migration + schema.ts
 * lockstep, all pinned together by `drone-taxonomy.test.ts`.
 *
 * SCOPE (#317): VOCABULARY ONLY. These tuples make drone assets / missions filterable
 * with the same lockstep discipline used for operator-action verbs and layer types.
 * Compliance-DOCUMENT capture + expiry (operator onboarding) is #318; mission matching
 * that consumes these is #320. The drone capability schema is brief §F.
 */

/**
 * Sensor / payload types an asset can carry (brief §F "sensor / payload capabilities").
 *   - eo           — electro-optical (daylight) camera.
 *   - ir           — infrared / thermal.
 *   - multispectral
 *   - lidar
 *   - sar          — synthetic-aperture radar.
 *   - gas          — gas / chemical detection.
 *   - comms-relay  — comms relay / bent-pipe payload.
 */
export const DRONE_SENSOR_TYPES = [
  'eo',
  'ir',
  'multispectral',
  'lidar',
  'sar',
  'gas',
  'comms-relay',
] as const;

export const DroneSensorTypeSchema = z.enum(DRONE_SENSOR_TYPES);
export type DroneSensorType = z.infer<typeof DroneSensorTypeSchema>;

/**
 * Airframe platform category (brief §F "platform … fixed-wing vs multirotor vs VTOL").
 *   - multirotor   — rotary, vertical take-off, short endurance.
 *   - fixed-wing   — long endurance / range, needs runway or catapult.
 *   - vtol         — vertical take-off + fixed-wing cruise (hybrid).
 *   - male         — medium-altitude long-endurance.
 */
export const DRONE_PLATFORM_CATEGORIES = [
  'multirotor',
  'fixed-wing',
  'vtol',
  'male',
] as const;

export const DronePlatformCategorySchema = z.enum(DRONE_PLATFORM_CATEGORIES);
export type DronePlatformCategory = z.infer<typeof DronePlatformCategorySchema>;

/**
 * Live-video downlink protocol (brief §F "downlink / feed … protocol"). Consumed by
 * the feed-embed phase (#322); `drone_assets.downlink_type` is nullable (an asset may
 * have no live downlink).
 */
export const DRONE_DOWNLINK_TYPES = [
  'rtmp',
  'srt',
  'rtsp',
  'webrtc',
] as const;

export const DroneDownlinkTypeSchema = z.enum(DRONE_DOWNLINK_TYPES);
export type DroneDownlinkType = z.infer<typeof DroneDownlinkTypeSchema>;

/**
 * Operator regulatory authorization level (brief §F "authorization level"). Canadian
 * RPAS tiers. VOCAB ONLY here — the operator profile that CARRIES an auth level, plus
 * compliance-document capture + expiry, is the operator-onboarding phase (#318); #317
 * mints the vocabulary so #318 has a stable FK target.
 *   - basic           — Basic operations certificate.
 *   - advanced        — Advanced operations certificate.
 *   - level-1-complex — Level 1 Complex operations.
 *   - sfoc-bvlos      — Special Flight Operations Certificate / BVLOS.
 */
export const OPERATOR_AUTH_LEVELS = [
  'basic',
  'advanced',
  'level-1-complex',
  'sfoc-bvlos',
] as const;

export const OperatorAuthLevelSchema = z.enum(OPERATOR_AUTH_LEVELS);
export type OperatorAuthLevel = z.infer<typeof OperatorAuthLevelSchema>;

/**
 * Coerce an arbitrary stored / wire value into a `DroneSensorType`, returning `null`
 * on any unexpected value (the FK guarantees a valid value at rest; this guards read
 * paths that surface a sensor type to clients). Mirrors `parseAgencyKind`.
 */
export function parseDroneSensorType(value: unknown): DroneSensorType | null {
  const parsed = DroneSensorTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Coerce to a `DronePlatformCategory`, `null` on unexpected input. */
export function parseDronePlatformCategory(value: unknown): DronePlatformCategory | null {
  const parsed = DronePlatformCategorySchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Coerce to a `DroneDownlinkType`, `null` on unexpected input. */
export function parseDroneDownlinkType(value: unknown): DroneDownlinkType | null {
  const parsed = DroneDownlinkTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Coerce to an `OperatorAuthLevel`, `null` on unexpected input. */
export function parseOperatorAuthLevel(value: unknown): OperatorAuthLevel | null {
  const parsed = OperatorAuthLevelSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
